"use client";

import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import type { VerdictHistoryEntry, VerdictStatus } from "./types";
import { COMMENT_MAX_LENGTH, useVerdictLabels } from "./meta";
import { FullHistoryRow } from "./HistoryItems";
import { ChipCommentEditor, DangerActionPicker } from "./ChipCommentEditor";
import { useDangerActions } from "./useDangerActions";
import { buildComment } from "./dangerActions";

interface AddCommentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Какой статус проставляем: от него зависит перечень признаков у школ со своим списком. */
  status: VerdictStatus | null;
  /** Последние записи с комментариями (показываются сверху как контекст) */
  recentEntries: VerdictHistoryEntry[];
  /** Сохранить комментарий. comment всегда непустой (валидация в модалке) */
  onSave: (comment: string) => void;
  /** Идёт запрос — блокируем кнопки */
  saving?: boolean;
}

/** Модалка «Добавить комментарий» — открывается после клика по Честно/Не честно (десктоп) */
export default function AddCommentModal({
  open,
  onOpenChange,
  status,
  recentEntries,
  onSave,
  saving = false,
}: AddCommentModalProps) {
  const labels = useVerdictLabels();
  const { catalog, labelOf, honestAutofill, plainList } = useDangerActions(status);

  // Свободный текст и упорядоченный список чипов — раздельно.
  // Счётчик считает только текст: чипы (danger-признаки) не учитываются.
  const [text, setText] = useState("");
  const [chips, setChips] = useState<string[]>([]);

  // Сброс полей при открытии (модалка — только создание записи).
  // Паттерн «правка стейта при смене пропа» в рендере, без эффекта.
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setText("");
      // У школ со своим перечнем «Честно» заполняется само — проктору остаётся сохранить.
      setChips(status === "honest" ? honestAutofill : []);
    }
  }

  const canSave = (text.trim().length > 0 || chips.length > 0) && !saving;

  const handleSave = () => {
    if (!canSave) return;
    // Чипы уезжают в строку как токены [TAG:key]; модалку закроет родитель.
    onSave(buildComment(text, chips));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px]" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="text-base font-semibold">{labels.addCommentTitle}</DialogTitle>
        </DialogHeader>

        {recentEntries.length > 0 && (
          <div className="flex min-h-[30px] flex-col items-start self-stretch rounded-[6px] border border-[#E5E5E5] bg-[#F5F5F5] overflow-hidden">
            {recentEntries.map((entry, i) => (
              <FullHistoryRow key={entry.id} entry={entry} striped={i % 2 === 0} />
            ))}
          </div>
        )}

        <div className="space-y-2">
          <ChipCommentEditor
            text={text}
            chips={chips}
            labelOf={labelOf}
            placeholder={labels.commentPlaceholder}
            removeLabel={labels.removeTag}
            onTextChange={(v) => setText(v.slice(0, COMMENT_MAX_LENGTH))}
            onRemoveChip={(i) => setChips((prev) => prev.filter((_, idx) => idx !== i))}
          />
          <p className="text-xs text-gray-400">
            {text.length}/{COMMENT_MAX_LENGTH}
          </p>
          <DangerActionPicker
            catalog={catalog}
            selected={chips}
            disabled={saving}
            plainList={plainList}
            onAdd={(raw) =>
              setChips((prev) => (prev.includes(raw) ? prev : [...prev, raw]))
            }
          />
        </div>

        <div className="flex items-center gap-2">
          <Button type="button" onClick={handleSave} disabled={!canSave}>
            {labels.save}
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={saving}
            onClick={() => onOpenChange(false)}
          >
            {labels.cancel}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
